/* tslint:disable:max-line-length */
import {Injectable, OnDestroy, Optional} from '@angular/core';
import {BehaviorSubject, map, switchMap} from 'rxjs';
import {distinctUntilChanged, filter} from 'rxjs/operators';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {MmProjectService} from './mm-project/mm-project.service';
import {Project as MmProject} from '@docma-mm-core/project/project';
import {MmProjectLoaderService} from './mm-project/mm-project-loader.service';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {toBoolean} from '@docma-mm-core/utils/utils';
import {PlatformModeStateService} from './mm-offline/services/platform-mode-state.service';
import {ArchiveSettings} from '@mydocma/core/src/lib/archive';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class DocuWareIntegrationService implements OnDestroy {

  private dwParameter: { DwActive: boolean, DwUrl: string, DwParameter: string, DwQuery: string };

  /** Whether DocuWare integration is enabled for the current project (placeholder: randomized on project change). */
  public readonly enabled$ = new BehaviorSubject<boolean>(false);

  set DwParameter(value: { DwActive: boolean, DwUrl: string, DwParameter: string, DwQuery: string }) {
    this.dwParameter = value;
  }

  get DwParameter() {
    return this.dwParameter;
  }

  constructor(private readonly platformModeStateService: PlatformModeStateService,
              private readonly mmProjectService: MmProjectService,
              private readonly projectLoaderService: MmProjectLoaderService,
              @Optional() private readonly archiveSetting: ArchiveSettings) {

    if (!!this.platformModeStateService.isLocalModeEnabled || (this.archiveSetting && !this.archiveSetting.enabled)) {
      return;
    }

    this.mmProjectService.currentProject.pipe(
      untilDestroyed(this),
      distinctUntilChanged((a, b) => this.projectIdentity(a) === this.projectIdentity(b)),
      switchMap(project =>
        this.projectLoaderService.projectSettings.pipe(filter(m => m.loadState.status === LoadStatusEnum.Loaded),
          map(ps => ({
            project,
            projectSettings: Object.fromEntries(ps.data.map(x => [x.Key, x.Setting]))
          }))
        )
      )
    ).subscribe(({ project, projectSettings }) => {
      if (!project) {
        this.enabled$.next(false);
        return;
      }
      const dwActive = toBoolean(projectSettings.DwActive);
      const allParamsSet: boolean = (!!projectSettings.DwUrl && !!projectSettings.DwParameter && !!projectSettings.DwQuery);
      this.enabled$.next(dwActive && allParamsSet);
      this.DwParameter = { DwActive: dwActive, DwUrl: projectSettings.DwUrl, DwParameter: projectSettings.DwParameter, DwQuery: projectSettings.DwQuery };
    });
  }

  ngOnDestroy(): void {
  }

  private projectIdentity(project: MmProject | null): string | null {
    if (!project) {
      return null;
    }
    const p = project as MmProject & { id?: string | number };
    const id = p.MyDocmaUid ?? p.Uid ?? p.SUid ?? p.Id ?? p.id;
    return id != null ? String(id) : null;
  }
}
